import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "../../services/authService";

// Async thunks
export const loginDoctor = createAsyncThunk(
  "auth/loginDoctor",
  async (credentials, thunkAPI) => {
    try {
      const response = await authService.login(credentials);
      // Store token and doctorId in localStorage
      localStorage.setItem("doctorToken", response.token);
      localStorage.setItem("doctorId", response.doctor.doctorId);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Login failed",
      );
    }
  },
);

export const registerDoctor = createAsyncThunk(
  "auth/registerDoctor",
  async (userData, thunkAPI) => {
    try {
      const response = await authService.register(userData);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Registration failed",
      );
    }
  },
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email, thunkAPI) => {
    try {
      const response = await authService.forgotPassword(email);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to send reset email",
      );
    }
  },
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ token, newPassword }, thunkAPI) => {
    try {
      const response = await authService.resetPassword(token, newPassword);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Password reset failed",
      );
    }
  },
);

const initialState = {
  isAuthenticated: !!localStorage.getItem("doctorToken"),
  doctor: localStorage.getItem("doctor")
    ? JSON.parse(localStorage.getItem("doctor"))
    : null,
  token: localStorage.getItem("doctorToken") || null,
  subscriptionActive:
    localStorage.getItem("subscriptionActive") === "true" || false,
  subscriptionExpiryDate:
    localStorage.getItem("subscriptionExpiryDate") || null,
  loading: false,
  error: null,
  successMessage: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logoutDoctor: (state) => {
      state.isAuthenticated = false;
      state.doctor = null;
      state.token = null;
      state.subscriptionActive = false;
      state.subscriptionExpiryDate = null;
      state.error = null;
      localStorage.removeItem("doctorToken");
      localStorage.removeItem("doctorId");
      localStorage.removeItem("doctor");
      localStorage.removeItem("subscriptionActive");
      localStorage.removeItem("subscriptionExpiryDate");
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginDoctor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginDoctor.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.doctor = action.payload.doctor;
        state.token = action.payload.token;
        state.subscriptionActive = action.payload.subscriptionActive;
        state.subscriptionExpiryDate = action.payload.subscriptionExpiryDate;
        state.error = null;
        localStorage.setItem(
          "subscriptionActive",
          action.payload.subscriptionActive,
        );
        localStorage.setItem(
          "subscriptionExpiryDate",
          action.payload.subscriptionExpiryDate || "",
        );
        // Store doctor object to localStorage
        localStorage.setItem("doctor", JSON.stringify(action.payload.doctor));
      })
      .addCase(loginDoctor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });

    // Register
    builder
      .addCase(registerDoctor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerDoctor.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Registration successful! Please login.";
        state.error = null;
      })
      .addCase(registerDoctor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Forgot Password
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Password reset link sent to your email!";
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Reset Password
    builder
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Password reset successful! Please login.";
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { logoutDoctor, clearError, clearSuccessMessage } =
  authSlice.actions;
export default authSlice.reducer;
